import './LoginForm.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { announce } from '../../a11y/LiveRegion';
import { type DoorKey, tDoor } from '../../copy/door';

/** Motivo da recusa do login (data/authClient.ts), repetido aqui: a feature não importa a camada de dados. */
export type SignInFailure = 'credentials' | 'throttled' | 'config' | 'network' | 'revoked';
export type ResetFailure = 'throttled' | 'config' | 'network';

export interface LoginFormProps {
  /** Entrar: null = deu certo (a página recarrega com a sessão); senão, o motivo. */
  signIn(email: string, password: string): Promise<SignInFailure | null>;
  /** "Esqueci a senha": null = pedido aceito (o texto é neutro, exista ou não a conta). */
  requestReset(email: string): Promise<ResetFailure | null>;
  /** Foca o e-mail ao montar (o formulário abriu por um clique). */
  autoFocus?: boolean;
}

const SIGN_IN_ERROR: Record<SignInFailure | 'empty', DoorKey> = {
  credentials: 'login.error.credentials',
  // a recusa do refresh token não acontece no login; se vier, o texto neutro de sempre
  revoked: 'login.error.credentials',
  throttled: 'login.error.throttled',
  config: 'login.error.config',
  network: 'login.error.network',
  empty: 'login.error.empty',
};
const RESET_ERROR: Record<ResetFailure | 'empty', DoorKey> = {
  throttled: 'reset.error.throttled',
  config: 'login.error.config',
  network: 'reset.error.network',
  empty: 'reset.error.empty',
};

type Mode = 'signin' | 'reset' | 'sent';

/**
 * O formulário da porta (ORD-F-153; DECISOES §84): e-mail, senha, "Entrar" e "Esqueci a senha". Sem "criar conta". O erro
 * fica visível abaixo dos campos (ligado a eles por `aria-describedby`, com `aria-invalid`) e é anunciado pela região viva
 * única do app (`announce(…, 'response')`, §15.3: o formulário não tem `role="alert"` nem `aria-live`). `noValidate`: a
 * conferência é nossa (o balão do navegador não segue a interface nem o texto neutro). O envio nunca navega (CSP
 * `form-action 'none'`): o login é pela API REST.
 */
export function LoginForm({ signIn, requestReset, autoFocus = false }: LoginFormProps) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DoorKey | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);

  useEffect(() => {
    // na abertura, o e-mail (se aberto por clique); a cada troca de modo, o título do modo novo
    if (first.current) {
      first.current = false;
      if (autoFocus) emailRef.current?.focus();
      return;
    }
    if (mode === 'sent') titleRef.current?.focus();
    else emailRef.current?.focus();
  }, [mode]);

  const fail = (key: DoorKey) => {
    setError(key);
    announce(tDoor(key), 'response');
  };

  const onSignIn = async (e: Event) => {
    e.preventDefault();
    if (busy) return;
    if (email.trim() === '' || password === '') {
      fail(SIGN_IN_ERROR.empty);
      return;
    }
    setBusy(true);
    setError(null);
    const reason = await signIn(email, password);
    // deu certo: a página recarrega com a sessão (o botão fica em "Entrando…")
    if (reason === null) return;
    setBusy(false);
    fail(SIGN_IN_ERROR[reason]);
  };

  const onReset = async (e: Event) => {
    e.preventDefault();
    if (busy) return;
    if (email.trim() === '') {
      fail(RESET_ERROR.empty);
      return;
    }
    setBusy(true);
    setError(null);
    const reason = await requestReset(email);
    setBusy(false);
    if (reason !== null) {
      fail(RESET_ERROR[reason]);
      return;
    }
    setMode('sent');
    announce(tDoor('reset.sent'), 'response');
  };

  const go = (m: Mode) => {
    setError(null);
    setPassword('');
    setMode(m);
  };

  const errorId = 'login-error';
  const invalid = error !== null;
  const described = invalid ? { 'aria-describedby': errorId, 'aria-invalid': 'true' as const } : {};

  if (mode === 'sent') {
    return (
      <div class="login-form" data-testid="login-form" data-mode="sent">
        <h2 class="login-form__title" ref={titleRef} tabIndex={-1}>
          {tDoor('reset.title')}
        </h2>
        <p class="login-form__sent" data-testid="login-reset-sent">
          {tDoor('reset.sent')}
        </p>
        <button type="button" class="login-form__link" data-testid="login-back" onClick={() => go('signin')}>
          {tDoor('reset.back')}
        </button>
      </div>
    );
  }

  const reset = mode === 'reset';
  return (
    <form
      class="login-form"
      data-testid="login-form"
      data-mode={mode}
      noValidate
      aria-labelledby="login-title"
      onSubmit={reset ? onReset : onSignIn}
    >
      <h2 class="login-form__title" id="login-title" ref={titleRef} tabIndex={-1}>
        {tDoor(reset ? 'reset.title' : 'login.title')}
      </h2>
      <p class="login-form__lede">{tDoor(reset ? 'reset.lede' : 'login.lede')}</p>
      <label class="login-form__label" for="login-email">
        {tDoor('login.email')}
      </label>
      <input
        ref={emailRef}
        class="login-form__input"
        id="login-email"
        name="email"
        type="email"
        inputMode="email"
        autoComplete="username"
        autoCapitalize="none"
        spellcheck={false}
        required
        value={email}
        onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
        data-testid="login-email"
        {...described}
      />
      {reset ? null : (
        <>
          <label class="login-form__label" for="login-password">
            {tDoor('login.password')}
          </label>
          <input
            class="login-form__input"
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onInput={(e) => setPassword((e.currentTarget as HTMLInputElement).value)}
            data-testid="login-password"
            {...described}
          />
        </>
      )}
      <p class="login-form__error" id={errorId} data-testid="login-error" hidden={!invalid}>
        {error ? tDoor(error) : ''}
      </p>
      <button
        type="submit"
        class="login-form__submit"
        data-testid="login-submit"
        aria-disabled={busy ? 'true' : undefined}
      >
        {tDoor(reset ? (busy ? 'reset.submitting' : 'reset.submit') : busy ? 'login.submitting' : 'login.submit')}
      </button>
      <button
        type="button"
        class="login-form__link"
        data-testid={reset ? 'login-back' : 'login-forgot'}
        onClick={() => go(reset ? 'signin' : 'reset')}
      >
        {tDoor(reset ? 'reset.back' : 'login.forgot')}
      </button>
    </form>
  );
}
